/**
 * System Health page — per docs/specs/system-health.md
 *
 * Grid of integration status cards (click for drill-down), DB pool detail,
 * 24h worker request-rate chart, recent health events timeline.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, RefreshCw } from 'lucide-react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { Chip } from '../components/chip';
import { Sparkline } from '../components/sparkline';
import { useToast } from '../components/toast';
import { useDbPool, useHealthEvents, useHealthIntegrations, useWorkerRate } from '../../lib/api';
import type { HealthIntegration } from '../../lib/types';
import {
    formatLatency,
    formatMetric,
    formatPercent,
    getHealthSourceNote,
    healthPulseStatus,
    healthStatusLabel,
    healthStatusTone,
    isUnavailableIntegration
} from './infra/health';
import { HealthDetailModal } from './health-detail-modal';

export function SystemHealthPage() {
    const integrations = useHealthIntegrations();
    const pool = useDbPool();
    const rate = useWorkerRate();
    const events = useHealthEvents();
    const [detailFor, setDetailFor] = useState<HealthIntegration | null>(null);
    const qc = useQueryClient();
    const { toast } = useToast();

    const integrationNameById = new Map(integrations.data?.map((i) => [i.id, i.name]) ?? []);
    const workerRateSnapshot = rate.data;
    const hasWorkerRateHistory =
        workerRateSnapshot?.available &&
        (workerRateSnapshot.points?.some(
            (point) => point.prod > 0 || point.dev > 0 || point.docs > 0
        ) ??
            false);

    const handleRunFullCheck = async () => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['admin', 'health', 'integrations'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'health', 'db-pool'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'health', 'worker-rate'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'health', 'events'] })
        ]);
        toast('Health snapshot refreshed', { kind: 'info' });
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>System health</h1>
                    <p>Green/yellow/red for every service we operate or depend on.</p>
                </div>
                <div className="page-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => void handleRunFullCheck()}
                    >
                        <RefreshCw size={14} /> Refresh checks
                    </button>
                    <a
                        className="btn btn-ghost"
                        href="https://dash.cloudflare.com"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <ExternalLink size={14} /> Cloudflare
                    </a>
                </div>
            </header>

            <section className="alert-bar">
                <div className="alert" data-severity="info">
                    <span className="msg">
                        Live probes are stitched from the worker, Supabase, Stripe, audio, and
                        Dropbox signals. DB pool occupancy and Cloudflare request history are
                        explicitly unavailable in this deployment, so those cards show source notes
                        instead of fake zeroes.
                    </span>
                    <span className="meta">read-only</span>
                </div>
            </section>

            <section className="card-grid-3" aria-label="Integration status">
                {integrations.data && integrations.data.length > 0 ? (
                    integrations.data.map((s) => (
                        <article
                            key={s.id}
                            className="card service-card"
                            onClick={() => setDetailFor(s)}
                            style={{ cursor: 'pointer' }}
                        >
                            <header className="service-card-head">
                                <span className="pulse-dot" data-status={toPulseStatus(s.status)} />
                                {s.status === 'unavailable' && (
                                    <span
                                        className="chip"
                                        data-tone="muted"
                                        style={{ marginLeft: 2, height: 16, fontSize: 9 }}
                                    >
                                        source unavailable
                                    </span>
                                )}
                                <div className="service-card-meta">
                                    <div className="service-card-name">
                                        {s.name}
                                        {s.critical && (
                                            <span
                                                className="chip"
                                                data-tone="muted"
                                                style={{ marginLeft: 6, height: 16, fontSize: 9 }}
                                            >
                                                critical
                                            </span>
                                        )}
                                    </div>
                                    {s.endpoint && (
                                        <div className="service-card-endpoint">{s.endpoint}</div>
                                    )}
                                </div>
                                <Chip tone={healthStatusTone(s.status)}>
                                    {healthStatusLabel(s.status)}
                                </Chip>
                            </header>
                            <div className="service-card-body">
                                <Stat label="req/min" value={formatMetric(s.req_per_min)} />
                                <Stat label="p95" value={formatLatency(s.p95_ms)} />
                                <Stat label="err %" value={formatPercent(s.error_pct)} />
                            </div>
                            {isUnavailableIntegration(s) ? (
                                <div className="form-hint" style={{ margin: 0 }}>
                                    {getHealthSourceNote(s)}
                                </div>
                            ) : (
                                <div className="service-card-spark">
                                    <Sparkline data={s.sparkline} width={240} height={24} />
                                </div>
                            )}
                        </article>
                    ))
                ) : (
                    <div className="empty-state">
                        <div className="title">No health probes available</div>
                    </div>
                )}
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Worker request rate · 24h</h2>
                        <span className="text-muted" style={{ fontSize: 11 }}>
                            {workerRateSnapshot?.available
                                ? 'prod + dev + docs'
                                : 'source unavailable'}
                        </span>
                    </header>
                    <div className="card-body" style={{ height: 240 }}>
                        {hasWorkerRateHistory && workerRateSnapshot ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={workerRateSnapshot.points}>
                                    <defs>
                                        <linearGradient id="prodRate" x1="0" y1="0" x2="0" y2="1">
                                            <stop
                                                offset="0%"
                                                stopColor="#3b82f6"
                                                stopOpacity={0.45}
                                            />
                                            <stop
                                                offset="100%"
                                                stopColor="#3b82f6"
                                                stopOpacity={0}
                                            />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid
                                        vertical={false}
                                        stroke="rgba(255,255,255,0.04)"
                                    />
                                    <XAxis
                                        dataKey="ts"
                                        tickLine={false}
                                        axisLine={false}
                                        tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        tickLine={false}
                                        axisLine={false}
                                        width={36}
                                        tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: '#12121a',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: 8,
                                            fontSize: 12
                                        }}
                                        labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="prod"
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        fill="url(#prodRate)"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="docs"
                                        stroke="#a855f7"
                                        strokeWidth={1.5}
                                        fillOpacity={0}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="empty-state" style={{ height: '100%' }}>
                                <div className="title">Request-rate history unavailable</div>
                                <div className="text-muted">
                                    {workerRateSnapshot?.message ??
                                        'Cloudflare analytics history is not wired in this deployment.'}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">DB connection pool</h2>
                    </header>
                    <div className="card-body">
                        {pool.data && (
                            <>
                                <dl className="kv-list">
                                    <dt>Active / max</dt>
                                    <dd>
                                        {formatMetric(pool.data.active)} /{' '}
                                        {formatMetric(pool.data.max)}
                                    </dd>
                                    <dt>Idle</dt>
                                    <dd>{formatMetric(pool.data.idle)}</dd>
                                    <dt>Waiting</dt>
                                    <dd>{formatMetric(pool.data.waiting)}</dd>
                                    <dt>Avg query</dt>
                                    <dd>{formatLatency(pool.data.avg_query_ms)}</dd>
                                    <dt>Slow queries (24h)</dt>
                                    <dd
                                        style={{
                                            color:
                                                (pool.data.slow_queries_24h ?? 0) > 10
                                                    ? 'var(--warning)'
                                                    : 'inherit'
                                        }}
                                    >
                                        {formatMetric(pool.data.slow_queries_24h)}
                                    </dd>
                                    <dt>Deadlocks (24h)</dt>
                                    <dd>{formatMetric(pool.data.deadlocks_24h)}</dd>
                                </dl>
                                <div className="form-hint" style={{ marginTop: 10 }}>
                                    {pool.data.message}
                                </div>
                                <div className="form-hint" style={{ marginTop: 6 }}>
                                    Live probe latency: {formatLatency(pool.data.probe_latency_ms)}
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </div>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Recent health events</h2>
                    <span className="text-muted" style={{ fontSize: 11 }}>
                        recent state transitions when probe history is available
                    </span>
                </header>
                <div className="card-body tight">
                    {events.data && events.data.length > 0 ? (
                        events.data.slice(0, 8).map((e) => (
                            <div
                                key={e.id}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '80px 1fr auto',
                                    gap: 10,
                                    padding: '10px 16px',
                                    borderBottom: '1px solid var(--border)',
                                    fontSize: 12,
                                    alignItems: 'center'
                                }}
                            >
                                <span className="mono text-muted">{e.ts}</span>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '16px 1fr',
                                        gap: 10,
                                        alignItems: 'center',
                                        minWidth: 0
                                    }}
                                >
                                    <span
                                        className="pulse-dot"
                                        data-status={toPulseStatus(e.to_status)}
                                    />
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ color: 'var(--text-primary)' }}>
                                            {integrationNameById.get(e.integration_id) ??
                                                e.integration_id}{' '}
                                            ·{' '}
                                            <span className="text-muted">
                                                {healthStatusLabel(e.from_status)} →{' '}
                                                {healthStatusLabel(e.to_status)}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                            {e.note}
                                        </div>
                                    </div>
                                </div>
                                <Chip tone={healthStatusTone(e.to_status)}>
                                    {healthStatusLabel(e.to_status)}
                                </Chip>
                            </div>
                        ))
                    ) : (
                        <div className="empty-state">
                            <div className="title">No recent state transitions yet</div>
                        </div>
                    )}
                </div>
            </section>

            <HealthDetailModal integration={detailFor} onClose={() => setDetailFor(null)} />
        </div>
    );
}

function toPulseStatus(s: HealthIntegration['status']) {
    return healthPulseStatus(s);
}

function Stat({ label, value }: { label: string; value: string }) {
    return (
        <div className="service-card-stat">
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
